// Utility functions for persisting step data to localStorage

const STORAGE_KEYS = {
  ONE_OFF_DATA: 'birdie_one_off_flow_data',
  SUBSCRIPTION_DATA: 'birdie_subscription_flow_data'
} as const;

export type FlowType = 'one-off' | 'subscription';

// Save step data to localStorage
export function saveStepData(flowType: FlowType, stepData: Record<string, any>): void {
  try {
    const key = flowType === 'one-off' ? STORAGE_KEYS.ONE_OFF_DATA : STORAGE_KEYS.SUBSCRIPTION_DATA;
    const dataToSave = {
      ...stepData,
      timestamp: Date.now(),
      flowType
    };
    localStorage.setItem(key, JSON.stringify(dataToSave));
  } catch (error) {
    console.warn('Failed to save step data to localStorage:', error);
  }
}

// Load step data from localStorage
export function loadStepData(flowType: FlowType): Record<string, any> {
  try {
    const key = flowType === 'one-off' ? STORAGE_KEYS.ONE_OFF_DATA : STORAGE_KEYS.SUBSCRIPTION_DATA;
    const savedData = localStorage.getItem(key);
    
    if (!savedData) {
      return {};
    }
    
    const parsedData = JSON.parse(savedData);
    
    // Check if data is less than 24 hours old (optional expiration)
    const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    if (parsedData.timestamp && (Date.now() - parsedData.timestamp > MAX_AGE)) {
      // Data is too old, clear it
      clearStepData(flowType);
      return {};
    }
    
    // Remove metadata before returning
    const { timestamp, flowType: savedFlowType, ...stepData } = parsedData;
    return stepData;
  } catch (error) {
    console.warn('Failed to load step data from localStorage:', error);
    return {};
  }
}

// Clear step data from localStorage
export function clearStepData(flowType: FlowType): void {
  try {
    const key = flowType === 'one-off' ? STORAGE_KEYS.ONE_OFF_DATA : STORAGE_KEYS.SUBSCRIPTION_DATA;
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear step data from localStorage:', error);
  }
}

// Clear all flow data (useful for complete cleanup)
export function clearAllFlowData(): void {
  clearStepData('one-off');
  clearStepData('subscription');
}

// Check if there's saved data for a flow type
export function hasSavedData(flowType: FlowType): boolean {
  try {
    const key = flowType === 'one-off' ? STORAGE_KEYS.ONE_OFF_DATA : STORAGE_KEYS.SUBSCRIPTION_DATA;
    return localStorage.getItem(key) !== null;
  } catch (error) {
    return false;
  }
}